import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import {
  ContentSection,
  DataSectionItem,
} from '@app/components/blocks/contentSection/ContentSection'
import { InfoAlert } from '@app/components/core/alert/InfoAlert'
import useMediaQuery from '@app/util/useMediaQuery'
import { ViewPlotModel } from 'functions/app/plot/view/index'
import { addressBuilder } from '@app/util/helpers'

export const Overview = ({ plot }: { plot: ViewPlotModel }) => {
  const isMobile = useMediaQuery()
  const largerGap = isMobile ? '2rem' : '2.5rem'
  const t = useTranslations().app
  const translations = t.plotPage.overviewTab
  const featuresKeys = t.plotPage.overviewFeaturesKeys
  const contactKeys = t.plotPage.contactInformationKeys
  const notSpecified = t.common.notSpecified
  const address = addressBuilder(plot.location.address)
  const geo = plot.location.geo
  const renderMap = geo.lat && geo.long

  const pageContentBlocks = plot.contentBlocks.filter(
    (b) => b.$position !== 'applicationTab' && b.$position !== 'documentsTab',
  )

  const featuresListItems: DataSectionItem[] = [
    {
      label: featuresKeys.offerType,
      content: plot.offerType?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: featuresKeys.buildingType,
      content: plot.buildingType?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: featuresKeys.developmentForm,
      content: plot.developmentForm?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: featuresKeys.ownershipType,
      content: plot.ownershipSituation?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: featuresKeys.livingEnvironment,
      content: plot.livingEnvironment?.name ?? notSpecified,
      styling: 'text',
    },
    {
      label: featuresKeys.numberOfUnits,
      content:
        plot.minNumberOfUnits && plot.maxNumberOfUnits
          ? `${plot.minNumberOfUnits} - ${plot.maxNumberOfUnits}`
          : plot.numberOfUnits
            ? String(plot.numberOfUnits)
            : notSpecified,
      styling: 'text',
    },
  ]

  const contactInformationListItems: DataSectionItem[] = [
    {
      label: contactKeys.providerType,
      content: plot.typeOfOrganisation ?? notSpecified,
      styling: 'text',
    },
    {
      label: contactKeys.organization,
      content: plot.organisationName ?? notSpecified,
      styling: 'text',
    },
    {
      label: contactKeys.contactPerson,
      content: plot.contactName ?? notSpecified,
      styling: 'text',
    },
    {
      label: contactKeys.email,
      content: plot.contactEmail ?? notSpecified,
      styling: 'text',
    },
    {
      label: contactKeys.website,
      content: plot.contactWebsite ?? notSpecified,
      styling: 'text',
    },
  ]

  return (
    <Column gap={largerGap} alignItems="stretch">
      {plot.infoTextBox && <InfoAlert message={plot.infoTextBox} />}
      {pageContentBlocks.map((block, index) => (
        <ContentSection
          key={block.id ?? index}
          section={{
            type: 'text',
            title: block.title,
            body: block.body,
            truncationThreshold: 0,
          }}
        />
      ))}
      {renderMap && (
        <Column alignItems="stretch">
          <ContentSection
            section={{
              type: 'location',
              title: translations.location,
              subtitle: address,
              lat: geo.lat,
              long: geo.long,
              marker: 'plot',
            }}
          />
        </Column>
      )}
      <ContentSection
        section={{
          type: 'data',
          title: translations.features,
          listItems: featuresListItems.map((item) => ({
            label: item.label,
            content: item.content,
            styling: item.styling,
          })),
        }}
      />
      <ContentSection
        section={{
          type: 'data',
          title: translations.contact,
          listItems: contactInformationListItems.map((item) => ({
            label: item.label,
            content: item.content,
            styling: item.styling,
          })),
        }}
      />
    </Column>
  )
}
